<template>
  <UDrawer
    v-model:open="isOpen"
    :dismissible="false"
    :handle="false"
    :ui="{
      content: 'w-full h-full',
      overlay: 'bg-black/50',
    }"
  >
    <template #content>
      <!-- Header -->
      <div
        class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700"
      >
        <h3 class="text-lg font-semibold">
          Personnaliser le Menu {{ menu.name }}
        </h3>
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-x"
          @click="closeDrawer"
        />
      </div>

      <!-- Main Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <div class="flex gap-6 mb-8">
          <div class="w-48 h-48 flex-shrink-0">
            <img
              :src="menu.image"
              :alt="menu.name"
              class="w-full h-full object-cover rounded-lg shadow-md"
            />
          </div>
          <div class="flex-1">
            <h2
              class="text-2xl font-bold text-main-color dark:text-main-color-400 mb-2"
            >
              {{ menu.name }}
            </h2>
            <p
              class="text-3xl font-bold text-main-color dark:text-main-color-400 mb-4"
            >
              {{ menu.price }}€
            </p>
          </div>
        </div>

        <h3 class="text-lg font-semibold mb-4">Contenu du menu</h3>
        <div class="space-y-4">
          <!-- Repas in the Menu -->
          <div
            v-for="content in menu.menu_content"
            :key="content.id"
            class="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-main-color transition-colors"
          >
            <div class="flex items-start gap-4">
              <img
                :src="
                  content.repas.image.startsWith('http')
                    ? content.repas.image
                    : getImageUrl(content.repas.image)
                "
                :alt="content.repas.name"
                class="w-20 h-20 rounded-md object-cover"
              />
              <div class="flex-1">
                <h4 class="font-semibold">{{ content.repas.name }}</h4>
                <p class="text-sm text-gray-500">
                  {{ getCustomizationSummary(content.id) }}
                </p>
              </div>
              <UButton
                size="sm"
                color="neutral"
                variant="outline"
                icon="i-lucide-pencil"
                @click="openMealCustomize(content)"
              >
                Personnaliser
              </UButton>
            </div>
          </div>
        </div>

        <!-- Sauces Selection -->
        <div v-if="availableSauces.length > 0" class="mb-8 p-6">
          <h3 class="text-lg font-semibold mb-4 flex justify-between">
            <span>Sauces</span>
            <span
              :class="{
                'text-green-500': totalSaucesSelected <= menu.sauce_number,
                'text-orange-500': totalSaucesSelected > menu.sauce_number,
              }"
            >
              {{ totalSaucesSelected }} / {{ menu.sauce_number }}
              <span
                v-if="totalSaucesSelected > menu.sauce_number"
                class="text-xs ml-1"
                >(Suppléments)</span
              >
            </span>
          </h3>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div
              v-for="sauce in availableSauces"
              :key="sauce.id"
              class="flex flex-col items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 hover:shadow-sm"
            >
              <NuxtImg
                :src="sauce.image"
                class="w-16 h-16 rounded-md object-cover"
              />
              <p class="text-sm font-medium text-center line-clamp-1">
                {{ sauce.name }}
              </p>
              <p class="text-xs text-main-color font-bold">
                +{{ sauce.price }}€
              </p>
              <div class="flex items-center gap-2 mt-auto">
                <UButton
                  size="2xs"
                  icon="i-lucide-minus"
                  variant="ghost"
                  :disabled="!selectedSauces[sauce.id]"
                  @click="decreaseSauce(sauce.id)"
                />
                <span class="text-sm font-medium w-4 text-center">{{
                  selectedSauces[sauce.id] || 0
                }}</span>
                <UButton
                  size="2xs"
                  icon="i-lucide-plus"
                  variant="ghost"
                  @click="increaseSauce(sauce.id)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Desserts Selection -->
        <div v-if="availableDesserts.length > 0" class="mb-8 p-6">
          <h3 class="text-lg font-semibold mb-4 flex justify-between">
            <span>Desserts</span>
            <span
              :class="{
                'text-green-500': totalDessertsSelected <= menu.dessert_number,
                'text-orange-500': totalDessertsSelected > menu.dessert_number,
              }"
            >
              {{ totalDessertsSelected }} / {{ menu.dessert_number }}
              <span
                v-if="totalDessertsSelected > menu.dessert_number"
                class="text-xs ml-1"
                >(Suppléments)</span
              >
            </span>
          </h3>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div
              v-for="dessert in availableDesserts"
              :key="dessert.id"
              class="flex flex-col items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 hover:shadow-sm"
            >
              <NuxtImg
                :src="dessert.image"
                class="w-16 h-16 rounded-md object-cover"
              />
              <p class="text-sm font-medium text-center line-clamp-1">
                {{ dessert.name }}
              </p>
              <p class="text-xs text-main-color font-bold">
                +{{ dessert.price }}€
              </p>
              <div class="flex items-center gap-2 mt-auto">
                <UButton
                  size="2xs"
                  icon="i-lucide-minus"
                  variant="ghost"
                  :disabled="!selectedDesserts[dessert.id]"
                  @click="decreaseDessert(dessert.id)"
                />
                <span class="text-sm font-medium w-4 text-center">{{
                  selectedDesserts[dessert.id] || 0
                }}</span>
                <UButton
                  size="2xs"
                  icon="i-lucide-plus"
                  variant="ghost"
                  @click="increaseDessert(dessert.id)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-6 border-t border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Coût total (avec suppléments) :
            </p>
            <p
              class="text-xl font-bold text-main-color dark:text-main-color-400"
            >
              {{ calculateTotalPrice().toFixed(2) }}€
            </p>
          </div>
          <UButton color="main" size="lg" @click="addMenuToCart">
            Ajouter le menu au panier
          </UButton>
        </div>
      </div>
    </template>
  </UDrawer>

  <MealCustomizeDrawer
    v-if="currentMealContent"
    v-model="isMealDrawerOpen"
    :meal="currentMealContent.repas"
    :existing-extras="customizations[currentMealContent.id]"
    :menu-context="{
      menuId: menu.id,
      menuName: menu.name,
      isMenuMode: true,
    }"
    :auto-add-to-cart="false"
    @meal-customized="onMealCustomized"
  />
</template>

<script setup lang="ts">
import type { Menu, MenuContent } from "~/models/menu";
import type { Repas, Ingredient } from "~/models/repas";
import type { Sauce } from "~/models/sauce";
import type { Dessert } from "~/models/dessert";

interface Props {
  menu: Menu;
  modelValue: boolean;
  // Existing customizations for editing mode
  existingCustomizations?: Array<{
    menuContentId: number;
    extras: Array<{ ingredient: Ingredient; quantity: number }>;
  }>;
  existingSauces?: Array<{ sauce: Sauce; quantity: number }>;
  existingDesserts?: Array<{ dessert: Dessert; quantity: number }>;
  autoAddToCart?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  autoAddToCart: true,
  existingCustomizations: undefined,
  existingSauces: undefined,
  existingDesserts: undefined,
});
const emit = defineEmits(["update:modelValue", "menu-customized"]);
const cartStore = useCartStore();
const { $api } = useNuxtApp();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const customizations = ref<
  Record<number, Array<{ ingredient: Ingredient; quantity: number }>>
>({});
const availableSauces = ref<Sauce[]>([]);
const availableDesserts = ref<Dessert[]>([]);
const selectedSauces = ref<Record<number, number>>({});
const selectedDesserts = ref<Record<number, number>>({});

// Initialize with existing customizations if provided (Edit Mode)
watch(
  () => props.existingCustomizations,
  (newVal) => {
    if (newVal) {
      const map: Record<
        number,
        Array<{ ingredient: Ingredient; quantity: number }>
      > = {};
      newVal.forEach((c) => {
        map[c.menuContentId] = c.extras;
      });
      customizations.value = map;
    } else {
      customizations.value = {};
    }
  },
  { immediate: true },
);

watch(
  () => props.existingSauces,
  (newVal) => {
    if (newVal) {
      const map: Record<number, number> = {};
      newVal.forEach((s) => (map[s.sauce.id] = s.quantity));
      selectedSauces.value = map;
    } else {
      selectedSauces.value = {};
    }
  },
  { immediate: true },
);

watch(
  () => props.existingDesserts,
  (newVal) => {
    if (newVal) {
      const map: Record<number, number> = {};
      newVal.forEach((d) => (map[d.dessert.id] = d.quantity));
      selectedDesserts.value = map;
    } else {
      selectedDesserts.value = {};
    }
  },
  { immediate: true },
);

// Fetch Sauces and Desserts
onMounted(async () => {
  await Promise.all([fetchSauces(), fetchDesserts()]);
});

const fetchSauces = async () => {
  try {
    const res = await $api.get("/sauce");
    if (res) {
      availableSauces.value = res.map((s: Sauce) => ({
        ...s,
        image: getImageUrl(s.image),
      }));
    }
  } catch (e) {
    console.error("Error fetching sauces", e);
  }
};

const fetchDesserts = async () => {
  try {
    const res = await $api.get("/dessert");
    if (res) {
      availableDesserts.value = res.map((d: Dessert) => ({
        ...d,
        image: getImageUrl(d.image),
      }));
    }
  } catch (e) {
    console.error("Error fetching desserts", e);
  }
};

const isMealDrawerOpen = ref(false);
const currentMealContent = ref<MenuContent | null>(null);

const openMealCustomize = (content: MenuContent) => {
  currentMealContent.value = content;
  isMealDrawerOpen.value = true;
};

const onMealCustomized = (data: {
  meal: Repas;
  extras: Array<{ ingredient: Ingredient; quantity: number }>;
}) => {
  if (currentMealContent.value) {
    customizations.value[currentMealContent.value.id] = data.extras;
  }
};

const getCustomizationSummary = (contentId: number) => {
  const extras = customizations.value[contentId];
  if (!extras || extras.length === 0) return "Aucune modification";
  return extras.map((e) => `${e.quantity}x ${e.ingredient.name}`).join(", ");
};

const totalSaucesSelected = computed(() =>
  Object.values(selectedSauces.value).reduce((a, b) => a + b, 0),
);
const totalDessertsSelected = computed(() =>
  Object.values(selectedDesserts.value).reduce((a, b) => a + b, 0),
);

const increaseSauce = (id: number) => {
  // Removed strict limit check
  selectedSauces.value[id] = (selectedSauces.value[id] || 0) + 1;
};
const decreaseSauce = (id: number) => {
  if (selectedSauces.value[id] > 0) {
    selectedSauces.value[id]--;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    if (selectedSauces.value[id] === 0) delete selectedSauces.value[id];
  }
};

const increaseDessert = (id: number) => {
  // Removed strict limit check
  selectedDesserts.value[id] = (selectedDesserts.value[id] || 0) + 1;
};
const decreaseDessert = (id: number) => {
  if (selectedDesserts.value[id] > 0) {
    selectedDesserts.value[id]--;
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    if (selectedDesserts.value[id] === 0) delete selectedDesserts.value[id];
  }
};

const calculatePricesForExtras = (
  items: Record<number, number>,
  available: { id: number; price: number }[],
  limit: number,
) => {
  const prices: number[] = [];
  Object.entries(items).forEach(([id, qty]) => {
    const item = available.find((i) => i.id === parseInt(id));
    if (item) {
      for (let i = 0; i < qty; i++) prices.push(item.price);
    }
  });
  // Sort Descending (Expensive first are free)
  prices.sort((a, b) => b - a);
  // Skip first 'limit' items (Included)
  const toPay = prices.slice(limit);
  // Sum
  return toPay.reduce((a, b) => a + b, 0);
};

const calculateTotalPrice = () => {
  let totalExtrasCost = 0;
  // Iterate over all customizations
  Object.entries(customizations.value).forEach(([contentId, extras]) => {
    const content = props.menu.menu_content.find(
      (c) => c.id === parseInt(contentId),
    );
    if (!content) return;

    const meal = content.repas;
    const originalIngredients: Record<string, number> = {};
    if (meal.ingredients) {
      const ingredientStrings = meal.ingredients
        .split(",")
        .map((s: string) => s.trim());
      ingredientStrings.forEach((ingredientString: string) => {
        const match = ingredientString.match(/^(\d+)\s+(.+)$/);
        if (match) {
          const quantity = parseInt(match[1]);
          const ingredientName = match[2].trim();
          originalIngredients[ingredientName.toLowerCase()] = quantity;
        }
      });
    }

    const extraCost = extras.reduce((sum, extra) => {
      const originalQty =
        originalIngredients[extra.ingredient.name.toLowerCase()] || 0;
      const additionalQty = Math.max(0, extra.quantity - originalQty);
      return sum + extra.ingredient.price * additionalQty;
    }, 0);

    totalExtrasCost += extraCost;
  });

  const saucesCost = calculatePricesForExtras(
    selectedSauces.value,
    availableSauces.value,
    props.menu.sauce_number,
  );

  const dessertsCost = calculatePricesForExtras(
    selectedDesserts.value,
    availableDesserts.value,
    props.menu.dessert_number,
  );

  return props.menu.price + totalExtrasCost + saucesCost + dessertsCost;
};

const closeDrawer = () => {
  isOpen.value = false;
};

const addMenuToCart = () => {
  // Convert map to array
  const customizationArray = Object.entries(customizations.value).map(
    ([contentId, extras]) => {
      const cId = parseInt(contentId);
      const content = props.menu.menu_content.find((c) => c.id === cId);
      return {
        menuContentId: cId,
        mealName: content?.repas.name,
        extras,
      };
    },
  );

  const saucesArray = Object.entries(selectedSauces.value).map(([id, qty]) => ({
    sauce: availableSauces.value.find((s) => s.id === parseInt(id))!,
    quantity: qty,
  }));

  const dessertsArray = Object.entries(selectedDesserts.value).map(
    ([id, qty]) => ({
      dessert: availableDesserts.value.find((d) => d.id === parseInt(id))!,
      quantity: qty,
    }),
  );

  emit("menu-customized", {
    menu: props.menu,
    customizations: customizationArray,
    sauces: saucesArray,
    desserts: dessertsArray,
  });

  if (props.autoAddToCart) {
    cartStore.addMenu(
      props.menu,
      customizationArray,
      saucesArray,
      dessertsArray,
    );
  }

  isOpen.value = false;
  // Reset
  customizations.value = {};
  selectedSauces.value = {};
  selectedDesserts.value = {};
};
</script>
